import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL, formatDate } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Plus, Users, Mail, Phone, Building2, Inbox, Edit, Trash2, UserPlus, MapPin, FileText, LayoutGrid, List, MoreVertical, FolderKanban, CheckSquare, Globe, Search, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { ViewClientDialog, ViewCompanyDialog, EditClientDialog, EditCompanyDialog } from '../components/clients/ClientDialogs';

// Extracted outside component to prevent re-mount on every render (fixes typing bug)
const ContactFormFields = ({ form, setForm, companies, customField, setCustomField, addCustomField }) => {
  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div>
        <label className="text-sm font-medium mb-2 block">Contact Name *</label>
        <Input 
          value={form.name || ''} 
          onChange={(e) => handleFieldChange('name', e.target.value)} 
          placeholder="Enter contact name" 
          required 
          autoComplete="off"
          data-testid="contact-name-input"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Contact Type *</label>
        <Select value={form.contact_type} onValueChange={(value) => handleFieldChange('contact_type', value === 'individual' ? 'individual' : 'corporate')}>
          <SelectTrigger><SelectValue placeholder="Select contact type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="corporate">Corporate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {form.contact_type === 'corporate' && (
        <div>
          <label className="text-sm font-medium mb-2 block">Company *</label>
          <Select value={form.company_id || '__none__'} onValueChange={(value) => handleFieldChange('company_id', value === '__none__' ? '' : value)}>
            <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {(companies || []).map((company) => (
                <SelectItem key={company.company_id} value={company.company_id}>{company.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Email</label>
          <Input 
            type="email" 
            value={form.email || ''} 
            onChange={(e) => handleFieldChange('email', e.target.value)} 
            placeholder="contact@example.com" 
            autoComplete="off"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Phone</label>
          <Input 
            value={form.phone || ''} 
            onChange={(e) => handleFieldChange('phone', e.target.value)} 
            placeholder="+91 98765 43210" 
            autoComplete="off"
          />
        </div>
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Designation</label>
        <Input 
          value={form.designation || ''} 
          onChange={(e) => handleFieldChange('designation', e.target.value)} 
          placeholder="e.g., CEO, Manager" 
          autoComplete="off"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Business Address</label>
        <Textarea 
          value={form.business_address || ''} 
          onChange={(e) => handleFieldChange('business_address', e.target.value)} 
          placeholder="Full business address" 
          rows={2} 
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">GST Number</label>
          <Input 
            value={form.gst_number || ''} 
            onChange={(e) => handleFieldChange('gst_number', e.target.value)} 
            placeholder="22AAAAA0000A1Z5" 
            autoComplete="off"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">PAN Number</label>
          <Input 
            value={form.pan_number || ''} 
            onChange={(e) => handleFieldChange('pan_number', e.target.value)} 
            placeholder="AAAAA0000A" 
            autoComplete="off"
          />
        </div>
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Notes</label>
        <Textarea 
          value={form.notes || ''} 
          onChange={(e) => handleFieldChange('notes', e.target.value)} 
          placeholder="Additional notes" 
          rows={2} 
        />
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Custom Fields</label>
        <div className="flex gap-2 mb-2">
          <Input placeholder="Field name" value={customField.key} onChange={(e) => setCustomField({ ...customField, key: e.target.value })} />
          <Input placeholder="Value" value={customField.value} onChange={(e) => setCustomField({ ...customField, value: e.target.value })} />
          <Button type="button" onClick={() => addCustomField(false)}>Add</Button>
        </div>
        {Object.keys(form.custom_fields || {}).length > 0 && (
          <div className="space-y-1">
            {Object.entries(form.custom_fields).map(([key, value]) => (
              <div key={key} className="text-sm bg-muted p-2 rounded"><strong>{key}:</strong> {value}</div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export const ClientsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);
  const [isEditClientDialogOpen, setIsEditClientDialogOpen] = useState(false);
  const [isEditCompanyDialogOpen, setIsEditCompanyDialogOpen] = useState(false);
  const [isViewClientDialogOpen, setIsViewClientDialogOpen] = useState(false);
  const [isViewCompanyDialogOpen, setIsViewCompanyDialogOpen] = useState(false);
  const [isAddContactToCompanyDialogOpen, setIsAddContactToCompanyDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [selectedCompanyForContact, setSelectedCompanyForContact] = useState(null);
  const [view, setView] = useState('clients');
  const [viewMode, setViewMode] = useState('tile'); // 'tile' or 'list'
  const [contactSearch, setContactSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [contactSort, setContactSort] = useState({ field: 'name', order: 'asc' });
  const [companySort, setCompanySort] = useState({ field: 'name', order: 'asc' });
  
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    contact_type: 'individual', // 'individual' or 'corporate'
    company_id: '',
    address: '',
    business_address: '',
    gst_number: '',
    pan_number: '',
    notes: '',
    custom_fields: {}
  });
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    industry: '',
    website: '',
    address: '',
    business_address: '',
    phone: '',
    gst_number: '',
    pan_number: '',
    custom_fields: {}
  });
  
  const [customField, setCustomField] = useState({ key: '', value: '' });

  useEffect(() => {
    if (searchParams.get('gmail_connected') === 'true') {
      toast.success('Gmail connected successfully');
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [clientsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/clients/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/companies/stats`, { withCredentials: true })
      ]);
      setClients(clientsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetClientForm = () => {
    setClientForm({
      name: '', email: '', phone: '', designation: '', contact_type: 'individual', company_id: '',
      address: '', business_address: '', gst_number: '', pan_number: '',
      notes: '', custom_fields: {}
    });
  };

  const resetCompanyForm = () => {
    setCompanyForm({
      name: '', industry: '', website: '', address: '', business_address: '',
      phone: '', gst_number: '', pan_number: '', custom_fields: {}
    });
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      // Map designation to position for backend compatibility
      const payload = {
        ...clientForm,
        position: clientForm.designation,
        // If corporate, company_id is required
        company_id: clientForm.contact_type === 'corporate' ? clientForm.company_id : (clientForm.company_id || null)
      };
      delete payload.designation;
      await axios.post(`${API_URL}/clients`, payload, { withCredentials: true });
      toast.success('Contact created successfully');
      setIsClientDialogOpen(false);
      setIsAddContactToCompanyDialogOpen(false);
      resetClientForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/companies`, companyForm, { withCredentials: true });
      toast.success('Company created successfully');
      setIsCompanyDialogOpen(false);
      resetCompanyForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const handleEditClient = (client) => {
    setEditingClient({ 
      ...client, 
      designation: client.position || '',
      contact_type: client.company_id ? 'corporate' : 'individual'
    });
    setIsEditClientDialogOpen(true);
  };

  const handleViewClient = (client) => {
    setViewingClient(client);
    setIsViewClientDialogOpen(true);
  };

  const handleViewCompany = (company) => {
    setViewingCompany(company);
    setIsViewCompanyDialogOpen(true);
  };

  const handleEditCompany = (company) => {
    setEditingCompany({ ...company });
    setIsEditCompanyDialogOpen(true);
  };

  const handleUpdateClient = async () => {
    try {
      const payload = {
        ...editingClient,
        position: editingClient.designation,
        company_id: editingClient.contact_type === 'corporate' ? editingClient.company_id : (editingClient.company_id || null)
      };
      delete payload.designation;
      delete payload.contact_type;
      await axios.patch(`${API_URL}/clients/${editingClient.client_id}`, payload, { withCredentials: true });
      toast.success('Contact updated successfully');
      setIsEditClientDialogOpen(false);
      setEditingClient(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleUpdateCompany = async () => {
    try {
      await axios.patch(`${API_URL}/companies/${editingCompany.company_id}`, editingCompany, { withCredentials: true });
      toast.success('Company updated successfully');
      setIsEditCompanyDialogOpen(false);
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update company');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/clients/${clientId}`, { withCredentials: true });
      toast.success('Contact deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This will fail if there are contacts associated.')) return;
    try {
      await axios.delete(`${API_URL}/companies/${companyId}`, { withCredentials: true });
      toast.success('Company deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/gmail/connect`, { withCredentials: true });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to connect Gmail');
    }
  };

  const addCustomField = (isCompany = false) => {
    if (!customField.key || !customField.value) return;
    if (isCompany) {
      setCompanyForm({
        ...companyForm,
        custom_fields: { ...companyForm.custom_fields, [customField.key]: customField.value }
      });
    } else {
      setClientForm({
        ...clientForm,
        custom_fields: { ...clientForm.custom_fields, [customField.key]: customField.value }
      });
    }
    setCustomField({ key: '', value: '' });
  };

  const openAddContactToCompany = (company) => {
    setSelectedCompanyForContact(company);
    setClientForm({
      ...clientForm,
      company_id: company.company_id,
      contact_type: 'corporate'
    });
    setIsAddContactToCompanyDialogOpen(true);
  };

  // Quick actions for contacts
  const handleQuickAction = (action, client) => {
    setIsViewClientDialogOpen(false);
    switch (action) {
      case 'task':
        navigate(`/tasks?new=true&client_id=${client.client_id}&client_name=${encodeURIComponent(client.name)}`);
        break;
      case 'project':
        navigate(`/projects?new=true&client_id=${client.client_id}&client_name=${encodeURIComponent(client.name)}`);
        break;
      case 'proposal':
        navigate(`/proposals?new=true&client_id=${client.client_id}&client_name=${encodeURIComponent(client.name)}${client.company_id ? `&company_id=${client.company_id}` : ''}`);
        break;
      default:
        break;
    }
  };

  const toggleContactSort = (field) => {
    setContactSort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));
  };
  const toggleCompanySort = (field) => {
    setCompanySort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));
  };

  const filteredClients = useMemo(() => {
    let result = clients;
    if (contactSearch) {
      const term = contactSearch.toLowerCase();
      result = result.filter(c => c.name?.toLowerCase().includes(term) || c.phone?.toLowerCase().includes(term));
    }
    result.sort((a, b) => {
      const aVal = (a[contactSort.field] || '').toString().toLowerCase();
      const bVal = (b[contactSort.field] || '').toString().toLowerCase();
      return contactSort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return result;
  }, [clients, contactSearch, contactSort]);

  const filteredCompanies = useMemo(() => {
    let result = companies;
    if (companySearch) {
      const term = companySearch.toLowerCase();
      result = result.filter(c => c.name?.toLowerCase().includes(term) || c.phone?.toLowerCase().includes(term));
    }
    result.sort((a, b) => {
      const aVal = (a[companySort.field] || '').toString().toLowerCase();
      const bVal = (b[companySort.field] || '').toString().toLowerCase();
      return companySort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return result;
  }, [companies, companySearch, companySort]);

  // ContactFormFields is now extracted outside the component to prevent re-mount issues

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Clients & Companies</h1>
          <p className="text-base text-muted-foreground">Manage your business relationships</p>
        </div>
        <div className="flex gap-2">
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="clients">Contacts</TabsTrigger>
            <TabsTrigger value="companies">Companies</TabsTrigger>
          </TabsList>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <Label htmlFor="view-mode" className="text-sm text-muted-foreground">View:</Label>
            <div className="flex items-center border rounded-md">
              <Button 
                variant={viewMode === 'tile' ? 'secondary' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('tile')}
                className="rounded-r-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* CONTACTS TAB */}
        <TabsContent value="clients" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search by name or phone..." className="pl-9" data-testid="contact-search" />
            </div>
            <Dialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-client-button">
                  <UserPlus className="mr-2 h-4 w-4" />
                  New Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Contact</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4">
                  <ContactFormFields form={clientForm} setForm={setClientForm} companies={companies} customField={customField} setCustomField={setCustomField} addCustomField={addCustomField} />
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsClientDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Create Contact</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {filteredClients.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">{contactSearch ? 'No matching contacts' : 'No contacts yet'}</h3>
              <p className="text-muted-foreground mb-6">{contactSearch ? 'Try a different search term' : 'Add your first contact to get started'}</p>
            </Card>
          ) : viewMode === 'tile' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client) => (
                <Card key={client.client_id} className="p-6 hover:shadow-md transition-shadow cursor-pointer relative" data-testid={`client-card-${client.client_id}`}>
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEditClient(client); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.client_id); }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div onClick={() => handleViewClient(client)}>
                    <div className="flex items-start justify-between mb-4 pr-20">
                      <div className="flex-1">
                        <h3 className="text-xl font-heading font-semibold mb-2">{client.name}</h3>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full mb-2 ${client.company_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                          {client.company_id ? 'Corporate' : 'Individual'}
                        </span>
                        {client.position && <p className="text-sm text-muted-foreground mb-2">{client.position}</p>}
                        {client.company_name && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Building2 className="h-4 w-4" />{client.company_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {client.email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" /><span className="truncate">{client.email}</span>
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />{client.phone}
                        </div>
                      )}
                      {client.gst_number && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="h-4 w-4" />GST: {client.gst_number}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 pt-3 border-t grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-lg font-bold">{client.project_count || 0}</p>
                        <p className="text-xs text-muted-foreground">Projects</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{client.proposal_count || 0}</p>
                        <p className="text-xs text-muted-foreground">Proposals</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{client.total_received ? `${(client.total_received / 1000).toFixed(0)}K` : '0'}</p>
                        <p className="text-xs text-muted-foreground">Received</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('name')}><div className="flex items-center gap-1">Name <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('contact_type')}><div className="flex items-center gap-1">Type <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('position')}><div className="flex items-center gap-1">Designation <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('company_name')}><div className="flex items-center gap-1">Company <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('email')}><div className="flex items-center gap-1">Email <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleContactSort('phone')}><div className="flex items-center gap-1">Phone <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.client_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => handleViewClient(client)}>
                      <td className="p-4 font-medium">{client.name}</td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${client.company_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                          {client.company_id ? 'Corporate' : 'Individual'}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground">{client.position || '-'}</td>
                      <td className="p-4 text-muted-foreground">{client.company_name || '-'}</td>
                      <td className="p-4 text-muted-foreground">{client.email || '-'}</td>
                      <td className="p-4 text-muted-foreground">{client.phone || '-'}</td>
                      <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button size="icon" variant="ghost" onClick={() => handleEditClient(client)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteClient(client.client_id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        {/* COMPANIES TAB */}
        <TabsContent value="companies" className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} placeholder="Search by name or phone..." className="pl-9" data-testid="company-search" />
            </div>
            <Dialog open={isCompanyDialogOpen} onOpenChange={setIsCompanyDialogOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" />New Company</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateCompany} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Company Name *</label>
                    <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="Enter company name" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Industry</label>
                      <Input value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })} placeholder="e.g., Technology, Finance" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Website</label>
                      <Input value={companyForm.website} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} placeholder="https://example.com" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Phone</label>
                    <Input value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Business Address</label>
                    <Textarea value={companyForm.business_address} onChange={(e) => setCompanyForm({ ...companyForm, business_address: e.target.value })} placeholder="Full business/registered address" rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">GST Number</label>
                      <Input value={companyForm.gst_number} onChange={(e) => setCompanyForm({ ...companyForm, gst_number: e.target.value })} placeholder="22AAAAA0000A1Z5" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">PAN Number</label>
                      <Input value={companyForm.pan_number} onChange={(e) => setCompanyForm({ ...companyForm, pan_number: e.target.value })} placeholder="AAAAA0000A" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Custom Fields</label>
                    <div className="flex gap-2 mb-2">
                      <Input placeholder="Field name" value={customField.key} onChange={(e) => setCustomField({ ...customField, key: e.target.value })} />
                      <Input placeholder="Value" value={customField.value} onChange={(e) => setCustomField({ ...customField, value: e.target.value })} />
                      <Button type="button" onClick={() => addCustomField(true)}>Add</Button>
                    </div>
                    {Object.keys(companyForm.custom_fields).length > 0 && (
                      <div className="space-y-1">
                        {Object.entries(companyForm.custom_fields).map(([key, value]) => (
                          <div key={key} className="text-sm bg-muted p-2 rounded"><strong>{key}:</strong> {value}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsCompanyDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Create Company</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {filteredCompanies.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">{companySearch ? 'No matching companies' : 'No companies yet'}</h3>
              <p className="text-muted-foreground mb-6">{companySearch ? 'Try a different search term' : 'Add your first company to get started'}</p>
            </Card>
          ) : viewMode === 'tile' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCompanies.map((company) => {
                const companyClients = clients.filter(c => c.company_id === company.company_id);
                return (
                  <Card 
                    key={company.company_id} 
                    className="p-6 hover:shadow-md transition-shadow relative cursor-pointer"
                    onClick={() => handleViewCompany(company)}
                    data-testid={`company-card-${company.company_id}`}
                  >
                    <div className="absolute top-4 right-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" onClick={() => openAddContactToCompany(company)} title="Add Contact">
                        <UserPlus className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleEditCompany(company)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDeleteCompany(company.company_id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold mb-2 pr-28">{company.name}</h3>
                      {company.industry && <p className="text-sm text-muted-foreground mb-2">{company.industry}</p>}
                      {company.website && (
                        <a 
                          href={company.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-sm text-accent hover:underline block mb-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {company.website}
                        </a>
                      )}
                      {company.business_address && (
                        <div className="flex items-start gap-2 text-sm text-muted-foreground mb-2">
                          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{company.business_address}</span>
                        </div>
                      )}
                      {company.gst_number && (
                        <div className="text-sm text-muted-foreground mb-1">
                          <strong>GST:</strong> {company.gst_number}
                        </div>
                      )}
                      {company.pan_number && (
                        <div className="text-sm text-muted-foreground mb-2">
                          <strong>PAN:</strong> {company.pan_number}
                        </div>
                      )}
                      <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-lg font-bold">{company.project_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Projects</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold">{company.proposal_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Proposals</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-green-600">{company.total_received ? `${(company.total_received / 1000).toFixed(0)}K` : '0'}</p>
                          <p className="text-xs text-muted-foreground">Received</p>
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <p className="text-xs text-muted-foreground">
                          {company.contact_count || companyClients.length} contact{(company.contact_count || companyClients.length) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleCompanySort('name')}><div className="flex items-center gap-1">Company Name <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleCompanySort('industry')}><div className="flex items-center gap-1">Industry <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleCompanySort('website')}><div className="flex items-center gap-1">Website <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleCompanySort('phone')}><div className="flex items-center gap-1">Phone <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleCompanySort('gst_number')}><div className="flex items-center gap-1">GST <ArrowUpDown className="h-3 w-3" /></div></th>
                    <th className="text-left p-4 font-medium">Contacts</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => {
                    const companyClients = clients.filter(c => c.company_id === company.company_id);
                    return (
                      <tr key={company.company_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => handleViewCompany(company)}>
                        <td className="p-4 font-medium">{company.name}</td>
                        <td className="p-4 text-muted-foreground">{company.industry || '-'}</td>
                        <td className="p-4 text-muted-foreground">
                          {company.website ? (
                            <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" onClick={(e) => e.stopPropagation()}>
                              {company.website}
                            </a>
                          ) : '-'}
                        </td>
                        <td className="p-4 text-muted-foreground">{company.phone || '-'}</td>
                        <td className="p-4 text-muted-foreground">{company.gst_number || '-'}</td>
                        <td className="p-4 text-muted-foreground">{companyClients.length}</td>
                        <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" onClick={() => openAddContactToCompany(company)} title="Add Contact">
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleEditCompany(company)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteCompany(company.company_id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* View Client Dialog */}
      <ViewClientDialog
        client={viewingClient}
        open={isViewClientDialogOpen}
        onOpenChange={setIsViewClientDialogOpen}
        onEdit={handleEditClient}
        onQuickAction={handleQuickAction}
      />

      {/* View Company Dialog */}
      <ViewCompanyDialog
        company={viewingCompany}
        clients={clients}
        open={isViewCompanyDialogOpen}
        onOpenChange={setIsViewCompanyDialogOpen}
        onEdit={handleEditCompany}
        onAddContact={openAddContactToCompany}
      />

      {/* Add Contact to Company Dialog */}
      {selectedCompanyForContact && (
        <Dialog open={isAddContactToCompanyDialogOpen} onOpenChange={(open) => { setIsAddContactToCompanyDialogOpen(open); if (!open) { resetClientForm(); setSelectedCompanyForContact(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Contact to {selectedCompanyForContact.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Contact Name *</label>
                <Input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="Enter contact name" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="contact@example.com" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Phone</label>
                  <Input value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} placeholder="+91 98765 43210" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Designation</label>
                <Input value={clientForm.designation} onChange={(e) => setClientForm({ ...clientForm, designation: e.target.value })} placeholder="e.g., CEO, Manager" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Business Address</label>
                <Textarea value={clientForm.business_address} onChange={(e) => setClientForm({ ...clientForm, business_address: e.target.value })} placeholder="Full business address" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">GST Number</label>
                  <Input value={clientForm.gst_number} onChange={(e) => setClientForm({ ...clientForm, gst_number: e.target.value })} placeholder="22AAAAA0000A1Z5" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">PAN Number</label>
                  <Input value={clientForm.pan_number} onChange={(e) => setClientForm({ ...clientForm, pan_number: e.target.value })} placeholder="AAAAA0000A" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea value={clientForm.notes} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} placeholder="Additional notes" rows={2} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsAddContactToCompanyDialogOpen(false); resetClientForm(); setSelectedCompanyForContact(null); }}>Cancel</Button>
                <Button type="submit">Add Contact</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Client Dialog */}
      <EditClientDialog
        client={editingClient}
        companies={companies}
        open={isEditClientDialogOpen}
        onOpenChange={setIsEditClientDialogOpen}
        onSave={(updatedClient, isLocalUpdate) => {
          if (isLocalUpdate) {
            setEditingClient(updatedClient);
          } else {
            handleUpdateClient();
          }
        }}
      />

      {/* Edit Company Dialog */}
      <EditCompanyDialog
        company={editingCompany}
        open={isEditCompanyDialogOpen}
        onOpenChange={setIsEditCompanyDialogOpen}
        onSave={(updatedCompany, isLocalUpdate) => {
          if (isLocalUpdate) {
            setEditingCompany(updatedCompany);
          } else {
            handleUpdateCompany();
          }
        }}
      />
    </div>
  );
};
